import { startTransition, useCallback, useEffect, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { useSearchParams } from 'react-router-dom';

type QueryParamValue = string | null;
type QueryParamUpdates = Map<string, QueryParamValue>;
type QueryParamOptions = Partial<{
  replace: boolean;
  clearAllKeys: boolean;
  clearAllSelectorKeys: string[];
}>;

type BatchQueueItem = {
  updates: QueryParamUpdates;
  options: QueryParamOptions;
  timestamp: number;
};

class QueryParamsBatcher {
  // eslint-disable-next-line no-use-before-define
  private static instance: QueryParamsBatcher;

  private batchQueue: BatchQueueItem[] = [];
  private isProcessing = false;
  private isBatchScheduled = false;
  private setSearchParamsRef:
    | ((params: URLSearchParams, options?: { replace?: boolean }) => void)
    | null = null;

  private constructor() {}

  static getInstance(): QueryParamsBatcher {
    if (!QueryParamsBatcher.instance) {
      QueryParamsBatcher.instance = new QueryParamsBatcher();
    }

    return QueryParamsBatcher.instance;
  }

  registerSetSearchParams(
    setSearchParams: (
      params: URLSearchParams,
      options?: { replace?: boolean }
    ) => void
  ) {
    this.setSearchParamsRef = setSearchParams;
  }

  addToBatch(updates: QueryParamUpdates, options: QueryParamOptions = {}) {
    // Add to queue with timestamp for ordering
    this.batchQueue.push({
      updates: new Map(updates), // Clone the map to avoid mutations
      options: structuredClone(options), // Clone options
      timestamp: performance.now()
    });

    // Schedule batch processing using queueMicrotask
    // This is better for React as it processes before the next render
    if (!this.isBatchScheduled) {
      this.isBatchScheduled = true;

      queueMicrotask(() => {
        // Wrap in startTransition for non-urgent updates
        startTransition(() => {
          this.processBatch();
        });
      });
    }
  }

  private applyBatchToParams(
    params: URLSearchParams,
    batch: BatchQueueItem
  ): URLSearchParams {
    // Handle clearAllKeys - starts fresh
    if (batch.options.clearAllKeys) {
      params = new URLSearchParams();
    }

    // Clear selector-based keys for this batch
    // clearAllSelectorKeys should clear all keys matching ANY of the provided selectors
    if (batch.options.clearAllSelectorKeys?.length) {
      const keysToDelete: string[] = [];

      batch.options.clearAllSelectorKeys.forEach(selector => {
        params.forEach((_, key) => {
          // Match exact selector or any key that starts with selector.
          if (key === selector || key.startsWith(selector + '.')) {
            keysToDelete.push(key);
          }
        });
      });

      keysToDelete.forEach(key => params.delete(key));
    }

    // Apply this batch's updates
    batch.updates.forEach((value, key) => {
      if (value === null) {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    });

    return params;
  }

  private async processBatch() {
    if (
      this.isProcessing ||
      this.batchQueue.length === 0 ||
      !this.setSearchParamsRef
    ) {
      return;
    }

    this.isProcessing = true;

    // Sort by timestamp to ensure sequential order
    this.batchQueue.sort((a, b) => a.timestamp - b.timestamp);

    // Start with current URL params
    let currentParams = new URLSearchParams(window.location.search);
    let finalReplace = true;

    // Process each batch item sequentially, each one building on the previous
    for (const batch of this.batchQueue) {
      currentParams = this.applyBatchToParams(currentParams, batch);

      // Track replace option - use the last one
      if (batch.options.replace !== undefined) {
        finalReplace = batch.options.replace;
      }
    }

    // Single update to URL with the final state
    this.setSearchParamsRef(currentParams, { replace: finalReplace });

    // Clear the queue
    this.batchQueue = [];
    this.isBatchScheduled = false;
    this.isProcessing = false;
  }

  // Force immediate processing (useful for cleanup)
  flush() {
    this.isBatchScheduled = false;

    // We absolutely need flushSync for synchronous execution to prevent race conditions
    // The warning about lifecycle is unavoidable when called from event handlers
    // but the behavior is correct and necessary for our use case

    flushSync(() => {
      this.processBatch();
    });
  }
}

const batcher = QueryParamsBatcher.getInstance();

export const useQueryParams = <
  T extends Record<string, unknown> = Record<string, string | null>
>(
  keys?: (keyof T & string)[],
  selectors?: string[]
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Register setSearchParams with the batcher on mount and updates
  useEffect(() => {
    batcher.registerSetSearchParams(setSearchParams);
  }, [setSearchParams]);

  const makeKey = useCallback((key: string, selectors?: string[]) => {
    return [...(selectors ?? []), key].join('.');
  }, []);

  const values = (keys ?? []).map(key =>
    searchParams.get(makeKey(key, selectors))
  );

  const flushQueryParams = useCallback(() => {
    batcher.flush();
  }, []);

  const params = useMemo(() => {
    return (keys ?? []).reduce(
      (acc, key, i) => {
        acc[key] = values[i] as T[typeof key] | null;
        return acc;
      },
      {} as { [K in keyof T]: T[K] | null }
    );
  }, values);

  const setQueryParams = (
    params: Partial<{ [K in keyof T]: T[K] | null }>,
    options?: QueryParamOptions
  ) => {
    const updates: QueryParamUpdates = new Map();

    for (const param in params) {
      const value = params[param];
      const key = makeKey(param, selectors);
      updates.set(key, value === null ? null : String(value));
    }

    batcher.addToBatch(updates, options);
  };

  return [params, setQueryParams, flushQueryParams] as const;
};
